/**
 * A single-select combobox over a list of strings.
 *
 * This was MunicipioSearch, generalised when the study form needed a second box
 * for Catastro's street register. Two lists, one keyboard model, one stylesheet:
 * a second bespoke dropdown is how two boxes in the same product end up
 * behaving differently — one with arrow keys and one mouse-only, which is
 * exactly what the legacy page's div-of-divs was.
 *
 * A real listbox, because both lists are long: 542 municipalities, and 3 680
 * streets in València alone. Matching lives in lib/search.ts so the accent rules
 * can be tested without a DOM.
 *
 * The typed text is internal state. To reset it — when the municipality changes
 * and the street register with it — remount with a `key`, rather than adding a
 * controlled `value` prop every caller then has to drive.
 */

import { useEffect, useId, useRef, useState } from "react";

import { highlightMatch, searchOptions } from "./lib/search";

// The component owns its styles, so any page can reuse it without also having to
// know to import some other page's stylesheet.
import "./Combobox.css";

interface Props {
  options: readonly string[];
  onSelect: (value: string) => void;
  /** The accessible name. Required: a wrapping <label> does not always
   *  associate, and an unnamed combobox is unusable on a screen reader. */
  label: string;
  placeholder?: string;
  disabled?: boolean;
  /** id of a hint element, for aria-describedby. */
  describedBy?: string;
}

export function Combobox({
  options, onSelect, label, placeholder, disabled, describedBy,
}: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);

  const hits = open ? searchOptions(options, query) : [];

  // Close on an outside click. Pointerdown rather than click, so the dropdown is
  // gone before a click elsewhere lands — otherwise selecting text in the page
  // leaves it hanging open.
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  function choose(value: string) {
    setQuery(value);
    setOpen(false);
    onSelect(value);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, hits.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      // Only when the dropdown is offering something. Otherwise Enter does
      // nothing visible on the map, and on the study form — where the box sits
      // inside a <form> — preventDefault would swallow the submit.
      const hit = hits[active];
      if (hit) {
        event.preventDefault();
        choose(hit);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="cb-wrap" ref={wrapRef}>
      <input
        type="text"
        className="cb-input"
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={hits.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={label}
        aria-describedby={describedBy}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {hits.length > 0 && (
        <ul className="cb-list" id={listId} role="listbox">
          {hits.map((value, i) => (
            <li key={value}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                // The whole name, unbroken. Without it the accessible name is
                // computed from the highlight markup below and comes out split
                // at the match — "Valè ncia" — because the <mark> and the <span>
                // are separate elements. Sighted users see the emphasis; a
                // screen reader was hearing the seam.
                aria-label={value}
                className={i === active ? "cb-option active" : "cb-option"}
                // Mousedown, not click: the input's blur would close the list
                // first and the click would land on nothing.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(value);
                }}
                onMouseEnter={() => setActive(i)}
              >
                {highlightMatch(value, query).map((part, j) =>
                  part.hit
                    ? <mark key={j}>{part.text}</mark>
                    : <span key={j}>{part.text}</span>,
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
